import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// routes
import { usePathname } from 'src/routes/hook';
import { useActiveLink } from 'src/routes/hook/use-active-link';
//
import { useNavigate } from 'react-router';
import { useGetCommonWithFilter } from 'src/api/common';
import { NavItem, NavItemDashboard } from './nav-item';
import { StyledSubheader, StyledMenu } from './styles';

import { HEADER } from '../../../config-layout';

// ----------------------------------------------------------------------

export default function NavList({
  item,
  offsetTop,
  onNavHover,
  isAnyMenuOpen,
  onMenuToggle,
  commonData,
}) {
  const pathname = usePathname();

  const nav = useBoolean();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const { path, children } = item;

  // Close menu on scroll
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
        nav.onFalse();
        onNavHover?.(false);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useActiveLink(path, false);

  const externalLink = path.includes('http');

  useEffect(() => {
    if (nav.value) {
      nav.onFalse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = () => {
    if (children) {
      nav.onTrue();
      onMenuToggle?.(item.title, true);
    }
  };

  const handleCloseMenu = () => {
    if (nav.value) {
      nav.onFalse();
      onMenuToggle?.(item.title, false);
    }
  };

  return (
    <>
      <NavItem
        item={item}
        offsetTop={offsetTop}
        active={active || nav.value}
        open={nav.value}
        externalLink={externalLink}
        isAnyMenuOpen={isAnyMenuOpen}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
        aria-expanded={nav.value}
      />

      {!!children && nav.value && !isScrolled && (
        // <Portal>
        <Fade in={nav.value}>
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              top: HEADER.H_DESKTOP,
              // zIndex: theme.zIndex.modal,
              overflow: 'hidden',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              flexDirection: 'column',
              width: '100vw',
              height: '100vh',
              margin: 0,
              padding: 0,
              borderRadius: 0,
            }}
          >
            <StyledMenu
              onMouseEnter={handleOpenMenu}
              onMouseLeave={handleCloseMenu}
              sx={{ display: 'flex', flexWrap: 'wrap' }}
            >
              {children.map((list, index) => {
                // Check if this is the first item in the children array
                if (index === 0) {
                  // For the first item, pass the mainTitle and mainSubtitle
                  return (
                    <NavSubList
                      key="projects"
                      items={list.items}
                      onClose={handleCloseMenu}
                      mainTitle={list.mainTitle}
                      mainSubtitle={list.mainSubtitle}
                      data={commonData}
                    />
                  );
                }

                // For other items, just render them without the title/subtitle
                return (
                  <NavSubList
                    key={list.subheader || `item-${index}`}
                    subheader={list.subheader}
                    items={list.items}
                    isDashboard={list.subheader === 'Dashboard'}
                    onClose={handleCloseMenu}
                  />
                );
              })}
            </StyledMenu>
          </Box>
        </Fade>
        // </Portal>
      )}
    </>
  );
}

NavList.propTypes = {
  item: PropTypes.object,
  offsetTop: PropTypes.bool,
  onNavHover: PropTypes.func,
  isAnyMenuOpen: PropTypes.bool,
  onMenuToggle: PropTypes.func,
  commonData: PropTypes.object,
};

// ----------------------------------------------------------------------

function NavSubList({ data, items, isDashboard, subheader, onClose, mainTitle, mainSubtitle }) {
  const pathname = usePathname();
  const theme = useTheme();
  const navigate = useNavigate();

  // Ensure we have items before rendering
  if (!items || !Array.isArray(items)) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '70vh',
        maxHeight: '70vh',
        width: '100%',
        maxWidth: '100%',
        margin: 0,
        padding: theme.spacing(4, 2),
        overflow: 'hidden',
        [theme.breakpoints.up('lg')]: {
          padding: theme.spacing(4, 6),
          flexDirection: 'row',
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          [theme.breakpoints.up('lg')]: {
            maxWidth: '100%',
            paddingRight: theme.spacing(4),
          },
          [theme.breakpoints.up('xl')]: {
            paddingRight: theme.spacing(6),
          },
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'left', width: '100%' }}>
          <Typography
            sx={{
              mt: 2,
              mb: 4,
              color: '#000000',
              fontWeight: '500',
              fontFamily: 'Satoshi Variable',
              fontSize: '42px',
            }}
          >
            {mainTitle}
          </Typography>
          <Typography
            sx={{
              color: '#666666',
              mb: 1,
              fontFamily: 'Satoshi Variable',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            {mainSubtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', // Always 3 columns
            gap: theme.spacing(3), // Consistent gap
            width: '100%',
            [theme.breakpoints.down('lg')]: {
              gap: theme.spacing(2),
            },
            [theme.breakpoints.down('md')]: {
              gap: theme.spacing(1.5),
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', // Ensure equal width columns
            },
            [theme.breakpoints.down('sm')]: {
              gap: theme.spacing(1),
            },
            '& > *': {
              width: '100%',
              minWidth: 0, // Allow items to shrink below their content size
            },
          }}
        >
          {subheader && <StyledSubheader disableSticky>{subheader}</StyledSubheader>}
          {items.map((item) =>
            isDashboard ? (
              <NavItemDashboard key={item.title} item={item} onClick={onClose} />
            ) : (
              <NavItem
                subItem
                key={item.title}
                item={item}
                active={pathname === `${item.path}/`}
                onClick={onClose}
              />
            )
          )}
        </Box>
      </Box>

      {/* Spotlight Image - Only show on extra large screens */}
      <Box
        sx={{
          display: 'none',
          [theme.breakpoints.up('lg')]: {
            display: 'block',
            width: '35%',
            height: '100%',
            borderRadius: 0,
            overflow: 'hidden',
            position: 'relative',
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 0,
            },
          },
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        onClick={() => {
          if (!data?.Header?.AmaranthineBannerLink) return;
          const imageUrl = `${data?.Header?.AmaranthineBannerLink}`;
          if (/^https?:\/\//.test(imageUrl)) {
            window.open(imageUrl, '_blank', 'noopener,noreferrer');
          } else {
            navigate(imageUrl);
          }
        }}
      >
        <img
          src={`${process.env.REACT_APP_HOST_API}${data?.Header?.AmaranthineBanner?.url}`}
          alt="Project Spotlight"
        />
      </Box>
    </Box>
  );
}

NavSubList.propTypes = {
  data: PropTypes.array,
  isDashboard: PropTypes.bool,
  items: PropTypes.array,
  onClose: PropTypes.func,
  subheader: PropTypes.string,
  mainTitle: PropTypes.string,
  mainSubtitle: PropTypes.string,
};
